import { useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import {
  TrendingUp,
  Car,
  Users,
  ChevronDown,
  ChevronUp,
  Award,
  Trophy,
  BarChart2,
  ExternalLink,
  Scale,
  Loader2,
  FileText,
} from 'lucide-react';
import { InvestorBadge, getBadgeTierFromDB, type BadgeTier } from './InvestorBadge';
import { Badge } from '../badges/Badge';
import { OwnershipCertificate, type CertificateData } from './OwnershipCertificate';
import { DAOGovernancePanel } from '../../components/dao/DAOGovernancePanel';
import { useInvestorPortfolio, type InvestmentRecord, type GiftRecord } from '../../hooks/useInvestorPortfolio';
import { Leaderboard } from './Leaderboard';
import { ContractPanel } from '../contracts/ContractPanel';
import TokenBalanceCard from '../tokens/TokenBalanceCard';
import { CampaignsPage } from '../campaigns/CampaignsPage';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CoInvestor {
  id: string;
  firstName: string;
  tier: BadgeTier;
}

interface VehicleInvestment {
  id: string;
  campaignId: string;
  make: string;
  model: string;
  year: number;
  imageUrl: string;
  ownershipPercent: number;
  investmentAmount: number;
  currentValueEstimate: number;
  goalAmount: number;
  currentAmount: number;
  coInvestorsCount: number;
  coInvestors: CoInvestor[];
  coInvestorsTotalAmount: number;
  status: 'Active' | 'Funded' | 'Disposed';
  investmentDate: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TIER_COLORS: Record<BadgeTier, string> = {
  friend: '#f43f5e',
  family: '#f97316',
  fam:    '#6366f1',
  legend: '#eab308',
};

const STATUS_CONFIG: Record<VehicleInvestment['status'], { bg: string; text: string; dot: string; label: string }> = {
  Active:   { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Campaign Active' },
  Funded:   { bg: 'bg-indigo-50',  text: 'text-indigo-700',  dot: 'bg-indigo-500',  label: 'Fully Funded'   },
  Disposed: { bg: 'bg-gray-100',   text: 'text-gray-500',    dot: 'bg-gray-400',    label: 'Pending'        },
};

function fmt(amount: number) {
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

/** Map a raw InvestmentRecord from the hook into the VehicleInvestment shape used by this component. */
function toVehicleInvestment(r: InvestmentRecord): VehicleInvestment {
  const words = r.title.split(' ');
  const make = words[0] ?? 'Vehicle';
  const model = words.slice(1).join(' ') || 'Unknown';
  const year = new Date(r.createdAt).getFullYear();
  const currentValueEstimate = r.goalAmount * (r.ownershipPercent / 100) * 1.05;
  const status: VehicleInvestment['status'] =
    r.status === 'completed' ? 'Funded' : r.status === 'pending' ? 'Disposed' : 'Active';

  const coInvestors: CoInvestor[] = r.coInvestors.map((ci, i) => ({
    id: `co-${r.contributionId}-${i}`,
    firstName: ci.name ? ci.name.split(' ')[0] : 'Anonymous',
    tier: 'friend' as BadgeTier,
  }));

  const coInvestorsTotalAmount = r.coInvestors.reduce((sum, ci) => sum + ci.amount, 0);

  return {
    id: r.contributionId,
    campaignId: r.goalId,
    make,
    model,
    year,
    imageUrl: r.imageUrl,
    ownershipPercent: r.ownershipPercent,
    investmentAmount: r.investmentAmount,
    currentValueEstimate,
    goalAmount: r.goalAmount,
    currentAmount: r.currentAmount,
    coInvestorsCount: coInvestors.length,
    coInvestors,
    coInvestorsTotalAmount,
    status,
    investmentDate: new Date(r.createdAt).toISOString().slice(0, 10),
  };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CoInvestorSection({
  coInvestors,
  totalAmount,
}: {
  coInvestors: CoInvestor[];
  totalAmount: number;
}) {
  if (coInvestors.length === 0) {
    return (
      <p className="pt-2 text-xs text-gray-400">No other investors on this campaign.</p>
    );
  }
  return (
    <div className="pt-2">
      {/* Summary line: total amount + count, no individual amounts */}
      <p className="text-xs text-gray-500 mb-2">
        <span className="font-semibold text-gray-700">{fmt(totalAmount)}</span> co-invested by{' '}
        {coInvestors.length} {coInvestors.length === 1 ? 'investor' : 'investors'}
      </p>
      {/* Tier dots + first names only */}
      <div className="flex flex-wrap gap-2">
        {coInvestors.map((ci) => (
          <div
            key={ci.id}
            className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-full px-2.5 py-1"
          >
            <div
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: TIER_COLORS[ci.tier] ?? '#C0C0C0' }}
            />
            <span className="text-xs font-medium text-gray-700">{ci.firstName}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function InvestmentCard({
  investment,
  onViewCertificate,
  onSignContract,
}: {
  investment: VehicleInvestment;
  onViewCertificate: (inv: VehicleInvestment) => void;
  onSignContract: (inv: VehicleInvestment) => void;
}) {
  const [showCoInvestors, setShowCoInvestors] = useState(false);
  const statusCfg = STATUS_CONFIG[investment.status];

  // P&L
  const gain = investment.currentValueEstimate - investment.investmentAmount;
  const gainPositive = gain >= 0;
  const roi = investment.investmentAmount > 0
    ? ((gain / investment.investmentAmount) * 100).toFixed(1)
    : '0.0';
  const roiPositive = parseFloat(roi) >= 0;

  // Monthly payment share estimate
  const monthlyPaymentShare = investment.goalAmount * 0.018 * (investment.ownershipPercent / 100);

  // Goal funding progress
  const fundingPct = investment.goalAmount > 0
    ? Math.min(100, Math.round((investment.currentAmount / investment.goalAmount) * 100))
    : 0;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
      {/* Car image */}
      <div className="relative h-44 overflow-hidden">
        <img
          src={investment.imageUrl}
          alt={`${investment.year} ${investment.make} ${investment.model}`}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        {/* Status badge */}
        <div
          className={`absolute top-3 right-3 flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${statusCfg.bg} ${statusCfg.text}`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
          {statusCfg.label}
        </div>
        {/* Vehicle name overlay */}
        <div className="absolute bottom-3 left-3 right-3">
          <p className="text-white font-bold text-lg leading-tight drop-shadow">
            {investment.year} {investment.make} {investment.model}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="p-4">
        {/* Goal funding progress bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-500 font-medium">{fundingPct}% funded</span>
            <span className="text-xs text-gray-400 tabular-nums">
              {fmt(investment.currentAmount)} / {fmt(investment.goalAmount)}
            </span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-500 rounded-full transition-all"
              style={{ width: `${fundingPct}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          {/* Ownership % — clicking opens certificate; with ROI pill + monthly estimate */}
          <button
            onClick={() => onViewCertificate(investment)}
            className="bg-indigo-50 rounded-xl p-3 text-left hover:bg-indigo-100 transition-colors group"
            title="View ownership certificate"
          >
            <div className="flex items-center justify-between mb-0.5">
              <p className="text-indigo-400 text-xs font-medium uppercase tracking-wide">Equity</p>
              {/* ROI pill */}
              <span
                className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                  roiPositive
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-red-100 text-red-600'
                }`}
              >
                {roiPositive ? '+' : ''}{roi}% ROI
              </span>
            </div>
            <p className="text-indigo-700 font-bold text-lg group-hover:underline">
              {investment.ownershipPercent}%
            </p>
            <p className="text-gray-400 text-xs mt-0.5">
              ~{fmt(monthlyPaymentShare)}/mo payment share
            </p>
          </button>

          <div className="bg-rose-50 rounded-xl p-3">
            <p className="text-rose-400 text-xs font-medium uppercase tracking-wide mb-0.5">Invested</p>
            <p className="text-rose-700 font-bold text-lg tabular-nums">{fmt(investment.investmentAmount)}</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-3">
            <p className="text-amber-500 text-xs font-medium uppercase tracking-wide mb-0.5">Est. Value</p>
            <p className="text-amber-700 font-bold text-lg tabular-nums">{fmt(investment.currentValueEstimate)}</p>
          </div>
          <div className={`rounded-xl p-3 ${gainPositive ? 'bg-emerald-50' : 'bg-red-50'}`}>
            <p
              className={`text-xs font-medium uppercase tracking-wide mb-0.5 ${
                gainPositive ? 'text-emerald-500' : 'text-red-400'
              }`}
            >
              Unrealized
            </p>
            <p className={`font-bold text-lg tabular-nums ${gainPositive ? 'text-emerald-700' : 'text-red-600'}`}>
              {gainPositive ? '+' : ''}
              {fmt(gain)}
            </p>
          </div>
        </div>

        {/* Co-investors toggle — total amount + count, no individual amounts */}
        <button
          onClick={() => setShowCoInvestors((v) => !v)}
          className="w-full flex items-center justify-between py-2 text-sm text-gray-500 hover:text-gray-700 border-t border-gray-100 transition-colors"
        >
          <span className="flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" />
            {investment.coInvestorsCount} co-investor{investment.coInvestorsCount !== 1 ? 's' : ''}
            {investment.coInvestorsCount > 0 && (
              <span className="text-gray-400 text-xs">
                · {fmt(investment.coInvestorsTotalAmount)} total
              </span>
            )}
          </span>
          {showCoInvestors ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {showCoInvestors && (
          <CoInvestorSection
            coInvestors={investment.coInvestors}
            totalAmount={investment.coInvestorsTotalAmount}
          />
        )}

        {/* Certificate button */}
        <button
          onClick={() => onViewCertificate(investment)}
          className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-indigo-600 border border-indigo-200 hover:bg-indigo-50 transition-colors"
        >
          <Award className="w-4 h-4" />
          View Certificate
          <ExternalLink className="w-3 h-3 opacity-50" />
        </button>

        {/* Sign agreement button */}
        <button
          onClick={() => onSignContract(investment)}
          className="mt-2 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors"
        >
          <FileText className="w-4 h-4" />
          Investor Agreement
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Portfolio Summary banner
// ---------------------------------------------------------------------------

function PortfolioSummaryBanner({
  totalInvested,
  totalEstValue,
  campaignCount,
  displayName,
  isRealUser,
}: {
  totalInvested: number;
  totalEstValue: number;
  campaignCount: number;
  displayName: string;
  isRealUser: boolean;
}) {
  const pnl = totalEstValue - totalInvested;
  const pnlPositive = pnl >= 0;
  const roi = totalInvested > 0 ? ((pnl / totalInvested) * 100).toFixed(1) : '0.0';
  const pnlSign = pnlPositive ? '+' : '';
  const pnlPercent = totalInvested > 0
    ? `${pnlSign}${((pnl / totalInvested) * 100).toFixed(1)}%`
    : '0.0%';

  return (
    <div className="bg-gradient-to-r from-indigo-950 via-indigo-900 to-navy-900 rounded-2xl px-6 py-4 mb-6 shadow-lg border border-indigo-800/40">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Left: core numbers */}
        <div className="flex items-center gap-5 flex-wrap">
          <Badge type="angel-investor" size={52} animate="pulse" />
          <div>
            <p className="text-indigo-300 text-xs font-semibold uppercase tracking-widest mb-0.5">
              Total Invested
            </p>
            <p className="text-white text-2xl font-bold tabular-nums">{fmt(totalInvested)}</p>
          </div>
          <div className="w-px h-10 bg-indigo-700/60 hidden sm:block" />
          <div>
            <p className="text-indigo-300 text-xs font-semibold uppercase tracking-widest mb-0.5">
              Total Est. Value
            </p>
            <p className="text-white text-2xl font-bold tabular-nums">{fmt(totalEstValue)}</p>
          </div>
          <div className="w-px h-10 bg-indigo-700/60 hidden sm:block" />
          <div>
            <p className="text-indigo-300 text-xs font-semibold uppercase tracking-widest mb-0.5">
              Unrealized P&amp;L
            </p>
            <p
              className={`text-2xl font-bold tabular-nums ${
                pnlPositive ? 'text-emerald-400' : 'text-red-400'
              }`}
            >
              {pnlSign}{fmt(pnl)}{' '}
              <span className="text-base font-semibold">({pnlPercent})</span>
            </p>
          </div>
          <div className="w-px h-10 bg-indigo-700/60 hidden sm:block" />
          <div>
            <p className="text-indigo-300 text-xs font-semibold uppercase tracking-widest mb-0.5">
              ROI
            </p>
            <p
              className={`text-2xl font-bold tabular-nums ${
                pnlPositive ? 'text-emerald-400' : 'text-red-400'
              }`}
            >
              {pnlSign}{roi}%
            </p>
          </div>
          <div className="w-px h-10 bg-indigo-700/60 hidden sm:block" />
          <div>
            <p className="text-indigo-300 text-xs font-semibold uppercase tracking-widest mb-0.5">
              Active Campaigns
            </p>
            <p className="text-white text-2xl font-bold tabular-nums">{campaignCount}</p>
          </div>
        </div>

        {/* Right: identity */}
        <div className="text-right flex-shrink-0">
          <p className="text-indigo-200 text-sm font-medium">
            Viewing as: <span className="text-white font-semibold">{displayName}</span>
          </p>
          {!isRealUser && (
            <p className="text-indigo-400 text-xs mt-0.5">seed investor — demo data</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main dashboard
// ---------------------------------------------------------------------------

export function InvestorDashboard() {
  // Clerk user — may be null/undefined if not signed in or Clerk not configured
  const { user } = useUser();
  const userId = user?.id ?? 'user_seed_investor_1';
  const investorName = user?.fullName ?? user?.firstName ?? 'Morgan Lee';
  const isRealUser = Boolean(user?.id);

  const { investments: rawInvestments, gifts, angelBadge, loading } = useInvestorPortfolio(userId);

  const investments: VehicleInvestment[] = rawInvestments.map(toVehicleInvestment);
  const activeInvestments = investments.filter((inv) => inv.status !== 'Disposed');
  const totalEquity = activeInvestments.reduce((sum, inv) => sum + inv.investmentAmount, 0);
  const vehiclesOwned = activeInvestments.length;
  const tier: BadgeTier = angelBadge
    ? angelBadge.badge_tier
    : getBadgeTierFromDB(0, totalEquity);
  const totalInvested = investments.reduce((sum, inv) => sum + inv.investmentAmount, 0);
  const totalEstValue = investments.reduce((sum, inv) => sum + inv.currentValueEstimate, 0);
  const totalGifted = gifts.reduce((sum, g) => sum + g.giftAmount, 0);

  const [badgeShowDetails, setBadgeShowDetails] = useState(false);
  const [certificateTarget, setCertificateTarget] = useState<VehicleInvestment | null>(null);
  const [contractSignTarget, setContractSignTarget] = useState<VehicleInvestment | null>(null);
  const [activeTab, setActiveTab] = useState<'gifts' | 'investments' | 'leaderboard' | 'governance'>('gifts');
  const [tosAgreed, setTosAgreed] = useState(false);
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);

  const isWelcome = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('welcome') === '1';
  const showWelcomeBanner = isWelcome && !welcomeDismissed && isRealUser;

  // Zero state: user has no contributions yet — drop straight into the campaign browser
  if (!loading && isRealUser && gifts.length === 0 && rawInvestments.length === 0) {
    return <CampaignsPage />;
  }

  const handleAgreeAndContinue = () => {
    // Strip ?welcome=1 from URL without reload
    const url = new URL(window.location.href);
    url.searchParams.delete('welcome');
    window.history.replaceState({}, '', url.toString());
    setWelcomeDismissed(true);
  };

  const TABS = [
    { id: 'gifts' as const,       label: 'My Gifts',       icon: Users },
    { id: 'investments' as const,  label: 'My Investments', icon: BarChart2 },
    { id: 'leaderboard' as const,  label: 'Leaderboard',   icon: Trophy },
    { id: 'governance' as const,   label: 'Governance',    icon: Scale },
  ];

  const certData: CertificateData | undefined = certificateTarget
    ? {
        investorName,
        carMake: certificateTarget.make,
        carModel: certificateTarget.model,
        carYear: certificateTarget.year,
        ownershipPct: certificateTarget.ownershipPercent,
        investmentAmount: certificateTarget.investmentAmount,
        date: certificateTarget.investmentDate,
      }
    : undefined;

  return (
    <main className="container mx-auto px-4 py-10 max-w-6xl">
      {/* ------------------------------------------------------------------ */}
      {/* Welcome banner — shown once after first contribution               */}
      {/* ------------------------------------------------------------------ */}
      {showWelcomeBanner && (
        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-2xl p-6 mb-8 text-white">
          <h2 className="text-xl font-bold mb-1">Welcome to your FAM dashboard! 🎉</h2>
          <p className="text-white/80 text-sm mb-5">
            You're officially part of the FAM. Complete your profile and agree to the platform terms to unlock all features.
          </p>

          {/* Profile fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-xs font-semibold text-white/70 mb-1 uppercase tracking-wide">Full Name</label>
              <div className="bg-white/10 rounded-lg px-3 py-2 text-sm text-white">
                {user?.fullName || <span className="text-white/50 italic">Not set — update in profile settings</span>}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-white/70 mb-1 uppercase tracking-wide">Email</label>
              <div className="bg-white/10 rounded-lg px-3 py-2 text-sm text-white">
                {user?.primaryEmailAddress?.emailAddress || '—'}
              </div>
            </div>
          </div>

          {/* Agreements */}
          <div className="space-y-2 mb-5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={tosAgreed}
                onChange={(e) => setTosAgreed(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded accent-white flex-shrink-0"
              />
              <span className="text-sm text-white/90">
                I agree to the FAM{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms of Service</a>{' '}
                and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a>.
                I understand FAM is a co-ownership platform, not a lender.
              </span>
            </label>
          </div>

          <button
            onClick={handleAgreeAndContinue}
            disabled={!tosAgreed}
            className="bg-white text-indigo-700 font-semibold px-6 py-2.5 rounded-full text-sm disabled:opacity-40 hover:bg-indigo-50 transition-colors"
          >
            Agree &amp; View My Dashboard
          </button>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Portfolio Summary banner (above tab bar)                            */}
      {/* ------------------------------------------------------------------ */}
      <PortfolioSummaryBanner
        totalInvested={totalInvested}
        totalEstValue={totalEstValue}
        campaignCount={activeInvestments.length}
        displayName={isRealUser ? investorName : 'Morgan Lee (seed investor)'}
        isRealUser={isRealUser}
      />

      {/* ------------------------------------------------------------------ */}
      {/* Portfolio Header                                                    */}
      {/* ------------------------------------------------------------------ */}
      <div className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700 rounded-3xl p-8 mb-10 text-white shadow-xl relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-white blur-3xl" />
          <div className="absolute bottom-0 left-1/3 w-48 h-48 rounded-full bg-indigo-300 blur-2xl" />
        </div>

        <div className="relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
            {/* Left: name + badge */}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Badge type="angel-investor" size={44} animate="pulse" />
                <p className="text-indigo-200 text-sm font-medium uppercase tracking-widest">
                  Supporter & Investor
                </p>
              </div>
              <h1 className="text-3xl font-bold mb-1">{investorName}</h1>
              <p className="text-indigo-300 text-sm mb-3">
                {gifts.length} campaign{gifts.length !== 1 ? 's' : ''} gifted · {vehiclesOwned} vehicle{vehiclesOwned !== 1 ? 's' : ''} owned
              </p>
              <InvestorBadge
                tier={tier}
                carsCount={vehiclesOwned}
                totalEquity={totalEquity}
                showDetails={badgeShowDetails}
                onToggleDetails={() => setBadgeShowDetails((v) => !v)}
                size="lg"
              />
            </div>

            {/* Right: portfolio numbers */}
            <div className="flex flex-col sm:flex-row gap-6 sm:gap-10">
              <div className="text-center sm:text-right">
                <p className="text-indigo-200 text-sm uppercase tracking-wider mb-1">Total Equity</p>
                <p className="text-4xl font-bold">{fmt(totalEquity)}</p>
                <div className="flex items-center gap-1 mt-1 sm:justify-end">
                  <TrendingUp className="w-4 h-4 text-emerald-300" />
                  <span className="text-emerald-300 text-sm font-medium">Across active positions</span>
                </div>
              </div>
              <div className="text-center sm:text-right">
                <p className="text-indigo-200 text-sm uppercase tracking-wider mb-1">Vehicles Owned</p>
                <p className="text-4xl font-bold">{vehiclesOwned}</p>
                <div className="flex items-center gap-1 mt-1 sm:justify-end">
                  <Car className="w-4 h-4 text-indigo-300" />
                  <span className="text-indigo-300 text-sm font-medium">Active investments</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Tab bar                                                             */}
      {/* ------------------------------------------------------------------ */}
      <div className="flex gap-1 bg-gray-100 rounded-2xl p-1 mb-8 overflow-x-auto">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 flex-1 justify-center px-4 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === id
                ? 'bg-white text-indigo-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Tab panels                                                          */}
      {/* ------------------------------------------------------------------ */}

      {/* Gifts panel */}
      {activeTab === 'gifts' && (
        <div>
          <div className="flex items-center gap-3 mb-6">
            <Badge type="angel-investor" size={32} animate="pulse" />
            <h2 className="text-xl font-bold text-gray-900">My Gifts</h2>
            <span className="ml-auto text-sm text-gray-400">{fmt(totalGifted)} total gifted</span>
          </div>
          {gifts.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <p className="text-lg font-medium">No gifts yet</p>
              <p className="text-sm mt-1">Browse campaigns and gift to someone you care about.</p>
              <a href="/campaigns" className="inline-block mt-4 bg-rose-500 text-white text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-rose-600 transition-colors">Browse Campaigns</a>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {gifts.map((gift: GiftRecord) => (
                <div key={gift.contributionId} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                  {gift.imageUrl && (
                    <div className="relative h-36 overflow-hidden">
                      <img src={gift.imageUrl} alt={gift.title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <div className="absolute bottom-2 left-3">
                        <Badge type="angel-investor" size={28} />
                      </div>
                    </div>
                  )}
                  <div className="p-4">
                    <p className="font-bold text-gray-900 text-sm leading-snug mb-1">{gift.title}</p>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-rose-600 font-bold text-lg">{fmt(gift.giftAmount)}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${gift.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {gift.status === 'completed' ? 'Funded' : 'Active'}
                      </span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-3">
                      <div
                        className="h-full bg-gradient-to-r from-rose-500 to-amber-500 rounded-full"
                        style={{ width: `${Math.min(100, Math.round((gift.currentAmount / gift.goalAmount) * 100))}%` }}
                      />
                    </div>
                    <a href={`/goal/${gift.goalId}`} className="block text-center text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors">
                      View Campaign →
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Investments panel */}
      {activeTab === 'investments' && (
        <div>
          <div className="flex items-center gap-3 mb-6">
            <BarChart2 className="w-5 h-5 text-indigo-500" />
            <h2 className="text-xl font-bold text-gray-900">My Investments</h2>
            {loading ? (
              <span className="ml-auto flex items-center gap-1.5 text-sm text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading…
              </span>
            ) : (
              <span className="ml-auto text-sm text-gray-400">{investments.length} vehicles</span>
            )}
          </div>

          {/* Token balances for funded vehicles */}
          {investments.filter(inv => inv.status === 'Funded').length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {investments.filter(inv => inv.status === 'Funded').map(inv => (
                <TokenBalanceCard
                  key={`token-${inv.id}`}
                  campaignId={inv.campaignId}
                  userId={userId}
                  viewAs="investor"
                />
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {investments.map((inv) => (
              <InvestmentCard
                key={inv.id}
                investment={inv}
                onViewCertificate={setCertificateTarget}
                onSignContract={setContractSignTarget}
              />
            ))}
          </div>
        </div>
      )}

      {/* Leaderboard panel */}
      {activeTab === 'leaderboard' && (
        <div className="max-w-2xl mx-auto">
          <Leaderboard />
        </div>
      )}

      {/* Governance panel */}
      {activeTab === 'governance' && <DAOGovernancePanel />}


      {/* ------------------------------------------------------------------ */}
      {/* Ownership Certificate modal                                         */}
      {/* ------------------------------------------------------------------ */}
      {certData && (
        <OwnershipCertificate
          data={certData}
          onClose={() => setCertificateTarget(null)}
        />
      )}

      {/* Investor agreement contract signing */}
      {contractSignTarget && (
        <ContractPanel
          contractType="investor"
          signerEmail=""
          signerName=""
          fields={[
            { name: 'vehicle', value: `${contractSignTarget.year} ${contractSignTarget.make} ${contractSignTarget.model}` },
            { name: 'campaignTitle', value: `${contractSignTarget.year} ${contractSignTarget.make} ${contractSignTarget.model}` },
            { name: 'investmentAmount', value: `$${contractSignTarget.investmentAmount.toLocaleString()}` },
            { name: 'ownershipPct', value: `${contractSignTarget.ownershipPercent}%` },
          ]}
          onComplete={() => setContractSignTarget(null)}
          onClose={() => setContractSignTarget(null)}
        />
      )}
    </main>
  );
}
